import { Card, CardContent, Input } from '@blink/ui';
import { searchHelp, type HelpArticle } from '@blink/shared';
import { ArrowLeft, Download, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Screen } from '@/components/common';
import data from '@/help/help-data.json';

const articles = data.articles as HelpArticle[];

function BackButton({ to }: { to: string }) {
  const navigate = useNavigate();
  return (
    <button type="button" aria-label="Back" className="order-first inline-flex h-10 w-10 items-center justify-center rounded-md text-white hover:bg-white/10" onClick={() => navigate(to)} data-testid="back">
      <ArrowLeft className="h-5 w-5" />
    </button>
  );
}

/** Driver-app help: the courier guides and the glossary, with search (English, D-0013 later). */
export function Help() {
  const [q, setQ] = useState('');
  const hits = useMemo(() => searchHelp(articles, q, 15), [q]);
  return (
    <Screen title={<h1 className="text-lg font-semibold">Help</h1>} actions={<BackButton to="/" />}>
      <div className="relative">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input type="search" className="h-12 ps-10 text-base" placeholder="Search help, e.g. cash, photo, damaged label" value={q} onChange={(e) => setQ(e.target.value)} data-testid="help-search" />
      </div>
      <Card>
        <CardContent className="divide-y pt-2" data-testid="help-list">
          {q
            ? hits.length === 0
              ? <p className="py-4 text-center text-muted-foreground">Nothing found. Try another word.</p>
              : hits.map((h) => (
                  <Link key={`${h.slug}-${h.sectionId}`} to={`/help/${h.slug}${h.sectionId ? `#${h.sectionId}` : ''}`} className="block py-3">
                    <div className="font-semibold text-primary">{h.title}</div>
                    {h.sectionTitle ? <div className="text-sm">{h.sectionTitle}</div> : null}
                    <div className="text-xs text-muted-foreground">{h.snippet}</div>
                  </Link>
                ))
            : articles.map((a) => (
                <Link key={a.slug} to={`/help/${a.slug}`} className="block py-3">
                  <div className="font-semibold text-primary">{a.title}</div>
                  <div className="text-xs text-muted-foreground">{a.summary}</div>
                </Link>
              ))}
        </CardContent>
      </Card>
      <a href="/manuals/Blink-Ops-Manual-Courier.pdf" download className="flex items-center justify-center gap-2 py-2 text-sm font-semibold text-primary">
        <Download className="h-4 w-4" /> Download the driver guide (PDF)
      </a>
    </Screen>
  );
}

export function HelpArticlePage() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView({ block: 'start' });
    else window.scrollTo(0, 0);
  }, [slug]);
  return (
    <Screen title={<h1 className="truncate text-lg font-semibold">{article?.title ?? 'Help'}</h1>} actions={<BackButton to="/help" />}>
      <Card>
        <CardContent className="pt-5">
          {article ? <div className="help-prose" data-testid="help-article" dangerouslySetInnerHTML={{ __html: article.html }} /> : <p className="text-muted-foreground">This help page does not exist.</p>}
        </CardContent>
      </Card>
    </Screen>
  );
}
